'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Check } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import {
  PLAN_ANNUAL_PRICES,
  PLAN_FEATURES,
  PLAN_PRICES,
  TRIAL_DAYS,
  type PlanKey,
} from '@/lib/billing/constants'
import { cn } from '@/lib/utils'
import { BillingToggle, type BillingInterval } from '@/components/marketing/billing-toggle'

// ─── Plan copy ──────────────────────────────────────────────────────────────

const PLAN_TAGLINES: Record<PlanKey, string> = {
  starter: 'For one product, getting started',
  growth: 'For multi-product or category coverage',
  pro: 'For several brands or larger coverage',
  agency: 'For agencies managing client brands',
}

const PAID_PLANS: { key: PlanKey; label: string; popular: boolean }[] = [
  { key: 'starter', label: 'Starter', popular: false },
  { key: 'growth', label: 'Growth', popular: true },
  { key: 'pro', label: 'Pro', popular: false },
  { key: 'agency', label: 'Agency', popular: false },
]

function getDisplayPrice(key: PlanKey, interval: BillingInterval): number {
  return interval === 'annual' ? Math.round(PLAN_ANNUAL_PRICES[key] / 12) : PLAN_PRICES[key]
}

function getAnnualSavings(key: PlanKey): number {
  return PLAN_PRICES[key] * 12 - PLAN_ANNUAL_PRICES[key]
}

// ─── PricingCards ───────────────────────────────────────────────────────────

/**
 * How the price is framed on each card:
 * - `base` (marketing default): the plain plan price, e.g. `$89 /mo`.
 * - `trialForward`: `$0 due today` → `then $X/mo` — for signup/onboarding
 *   surfaces where removing the "pay now" sticker shock matters.
 */
export type PriceMode = 'base' | 'trialForward'

export interface PricingCardsProps {
  /** Which billing interval's price shows. */
  billing: BillingInterval
  /** Price framing — defaults to `base` (plain price). See {@link PriceMode}. */
  priceMode?: PriceMode
  className?: string
}

/**
 * PricingCards — the 4 tier cards (Starter/Growth/Pro/Agency), Growth badged
 * "Most Popular". The price framing is controlled by `priceMode`: `base`
 * (default) shows the plain `$X/mo` for marketing; `trialForward` shows
 * `$0 due today → then $X/mo` for onboarding. The secondary line follows the
 * billing toggle and, on annual, states the per-plan dollar savings.
 *
 * WHEN: any page rendering the 4 paid plans (home teaser, `/pricing`,
 * onboarding via `priceMode="trialForward"`).
 * NEVER: hand-roll a second card grid from `PLANS`/`PLAN_PRICES` — extend
 * this component's prop surface instead so every surface stays in sync.
 */
export function PricingCards({ billing, priceMode = 'base', className }: PricingCardsProps) {
  return (
    <div className={cn('grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4', className)}>
      {PAID_PLANS.map((plan) => {
        const displayPrice = getDisplayPrice(plan.key, billing)
        const savings = getAnnualSavings(plan.key)
        const isGrowth = plan.key === 'growth'
        const isAgency = plan.key === 'agency'
        const isStarter = plan.key === 'starter'

        return (
          <Card
            key={plan.key}
            id={plan.key}
            className={cn('scroll-mt-24 flex flex-col', isGrowth && 'ring-2 ring-primary')}
          >
            <CardHeader>
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-base font-medium">{plan.label}</CardTitle>
                {isGrowth && <Badge variant="primary">Most Popular</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">{PLAN_TAGLINES[plan.key]}</p>

              {priceMode === 'trialForward' && isStarter ? (
                /* Trial-forward — $0-today anchor only on Starter (the sole trial plan);
                   other plans charge at checkout and fall through to the plain price. */
                <div className="pt-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="type-h2 font-semibold tabular-nums text-foreground">$0</span>
                    <span className="text-sm text-muted-foreground">due today</span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    then{' '}
                    <span className="font-medium tabular-nums text-foreground">
                      ${displayPrice}/mo
                    </span>
                    {billing === 'annual' ? (
                      <span>{` · $${PLAN_ANNUAL_PRICES[plan.key]}/yr, save $${savings}`}</span>
                    ) : null}
                  </p>
                </div>
              ) : (
                /* Base — the plain plan price */
                <div className="pt-1">
                  <div className="flex items-baseline gap-1">
                    <span className="type-h2 font-semibold tabular-nums text-foreground">
                      ${displayPrice}
                    </span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {billing === 'annual'
                      ? `$${PLAN_ANNUAL_PRICES[plan.key]}/yr billed annually · save $${savings}`
                      : 'billed monthly'}
                  </p>
                </div>
              )}
            </CardHeader>

            <CardContent className="flex-1 flex flex-col gap-3">
              <ul className="space-y-1.5 text-sm">
                {PLAN_FEATURES[plan.key].map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check className="mt-0.5 size-4 shrink-0 text-emerald" aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {isAgency && (
                <p className="text-xs text-muted-foreground">
                  Running an agency? White-label client reports are available on Agency —{' '}
                  <Link
                    href="/contact"
                    className="text-foreground underline decoration-muted-foreground/40 underline-offset-2 transition-colors duration-200 hover:decoration-foreground"
                  >
                    let&apos;s talk
                  </Link>
                  .
                </p>
              )}
            </CardContent>

            <CardFooter className="flex-col items-stretch gap-2">
              <Button
                render={<Link href={`/auth/login?mode=signup&ref=pricing-${plan.key}`} />}
                nativeButton={false}
                variant={isGrowth ? 'default' : 'outline'}
                className="w-full"
              >
                {isStarter ? `Start ${TRIAL_DAYS}-day free trial` : 'Get Started'}
              </Button>
              {isStarter && (
                <p className="text-center text-xs text-muted-foreground">
                  No charge today · Cancel anytime
                </p>
              )}
            </CardFooter>
          </Card>
        )
      })}
    </div>
  )
}

// ─── PricingCardsGroup ──────────────────────────────────────────────────────

export interface PricingCardsGroupProps {
  /** Price framing — defaults to `base` (plain price). See {@link PriceMode}. */
  priceMode?: PriceMode
  className?: string
  cardsClassName?: string
}

/**
 * PricingCardsGroup — `BillingToggle` + `PricingCards`, owning the billing
 * interval state. Used by both the home pricing teaser and `/pricing` so the
 * toggle wiring never drifts between the two.
 */
export function PricingCardsGroup({
  priceMode = 'base',
  className,
  cardsClassName,
}: PricingCardsGroupProps) {
  const [interval, setInterval] = useState<BillingInterval>('monthly')

  return (
    <div className={className}>
      <div className="flex justify-center">
        <BillingToggle value={interval} onChange={setInterval} />
      </div>
      <PricingCards
        billing={interval}
        priceMode={priceMode}
        className={cn('marketing-gap-content', cardsClassName)}
      />
    </div>
  )
}
